import { useDispatch, useSelector } from "react-redux";
import { Box, Text, useThemeUI } from "theme-ui";
import nl2br from "react-nl2br";
import get from "lodash/fp/get";
import has from "lodash/fp/has";
import negate from "lodash/fp/negate";

import { selectCustomisationNotifications, selectUserChoices } from "store/app";
import {
  displayModal,
  displayFullscreenSelectionModal,
} from "store/app/appSlice";
import { FieldsSelector } from "components/Forms";
import { HelpButton, LearnMore, Notifications } from "components";
import { useTranslation } from "hooks";
import { FORM_COMPONENTS } from "utils/constants";
import { shouldBeDisplayedForLocale } from "utils";
import { InfoIcon } from "components/icons";

export default function Fields({
  userChoiceValues,
  selectedCustomisation,
  focusedField: focusedFields = [],
  setFocusedField,
  goBackFocusedFields,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const locale = useSelector((state) => state.app.locale);
  const userChoices = useSelector(selectUserChoices);
  // const isOutOfStock = useSelector(selectIsOutOfStock);

  // Find notifications based on isOutOfStock status
  const notifications = useSelector(selectCustomisationNotifications);

  // Filter notifications based on type (product or customisation)
  // const productNotifications = notifications.filter(
  //   (notification) => notification.type === "product"
  // );

  const customisationNotifications = notifications.filter(
    (notification) => notification.type === "customisation"
  );

  const [focusedField, ..._restFocusedFields] = focusedFields;

  // filteredFields is multiple when rendering a group of nested fields eg Goalie Gear Colors
  const filteredFields = (selectedCustomisation?.fields || [])
    .filter(({ name }) => !focusedField || name === focusedField)
    .filter(negate(has("preventRender")));

  const singularField = filteredFields.length === 1 ? filteredFields[0] : null;

  const learnMore = shouldBeDisplayedForLocale(locale)(
    selectedCustomisation?.learnMore
  );

  const customisationsVisited = useSelector(
    get(["app", "customisationsVisited"])
  );

  if (
    singularField?.initialModal &&
    !customisationsVisited.includes(singularField?.initialModal?.content?.id) &&
    !userChoiceValues[singularField?.name]
  ) {
    dispatch(
      displayFullscreenSelectionModal({
        ...singularField?.initialModal?.content,
        customisation: selectedCustomisation,
      })
    );
  }

  const hasOptionalFields =
    filteredFields.length > 1 &&
    filteredFields.some(({ component, isRequired }) => {
      return (
        component === FORM_COMPONENTS.GOALIE_GEAR_COLOR_OPTIONS &&
        isRequired === false
      );
    });

  return (
    <>
      {filteredFields.map((field) => (
        <FieldsSelector
          key={field.name}
          customisationTitle={selectedCustomisation.title}
          component={field.component}
          userChoices={userChoices}
          userChoiceValues={userChoiceValues}
          name={field.name}
          placeholder={field.placeholder}
          as={field.as}
          type={field.type}
          initialValue={field.initialValue}
          maxLength={field.maxLength}
          isRequired={field.isRequired}
          options={field.options}
          customOrdering={field.customOrdering}
          fields={field.fields}
          label={field.label}
          labelIcon={field.labelIcon}
          checkedValue={field.checkedValue}
          uncheckedValue={field.uncheckedValue}
          isDisabledIf={field.isDisabledIf}
          width={field.width}
          focusedField={focusedFields}
          setFocusedField={setFocusedField}
          goBackFocusedFields={goBackFocusedFields}
          allowUnselect={field.allowUnselect}
          hint={field.hint}
          info={field.info}
          hasOptionalFields={hasOptionalFields}
          defaultValueToSetOnRender={field.defaultValueToSetOnRender}
          toolTip={field.toolTip}
          goBackOnSelect={field.goBackOnSelect}
          props={field.props}
        />
      ))}

      {selectedCustomisation?.help && (
        <Box>
          <HelpButton
            onClick={() =>
              dispatch(
                displayModal({
                  modal: "Modal",
                  modalProps: selectedCustomisation.help,
                })
              )
            }
          >
            {selectedCustomisation.help.buttonLabel}
          </HelpButton>
        </Box>
      )}

      {learnMore && (
        <LearnMore
          href={learnMore.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {learnMore.buttonLabel}
        </LearnMore>
      )}

      {selectedCustomisation.hint && (
        <Text
          variant="copy2"
          key="hint"
          sx={{
            color: "grey5",
            fontFamily: "roboto",
            lineHeight: 1,
            textAlign: ({ isMobile }) => (isMobile ? "center" : "initial"),
          }}
        >
          {nl2br(selectedCustomisation.hint)}
        </Text>
      )}

      {selectedCustomisation.hintWithIcon && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <InfoIcon strokeOverride="black" />
          <Text
            variant="copy2"
            key="hint"
            sx={{
              color: "black",
              fontFamily: "roboto",
              fontWeight: "300",
              lineHeight: "18px",
              maxWidth: "200px",
              textAlign: ({ isMobile }) => (isMobile ? "center" : "initial"),
            }}
          >
            {nl2br(selectedCustomisation.hintWithIcon)}
          </Text>
        </Box>
      )}

      {hasOptionalFields && (
        <Text
          variant="h5Light"
          key="note"
          sx={{
            fontFamily: "dinCondensed",
            textAlign: ({ isMobile }) => (isMobile ? "center" : "initial"),
          }}
        >
          *{t("OPTIONAL")}
        </Text>
      )}

      {/* {isMobile && isOutOfStock && customisationNotifications.length > 0 && (
        <Notifications notifications={productNotifications} />
      )} */}

      {isMobile && customisationNotifications.length > 0 && (
        <Notifications notifications={customisationNotifications} />
      )}
    </>
  );
}
